import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useMutation } from '@ts-core/lib/apollo';

import { getErrorMessage } from '@ts-core/lib/apollo';
import { useSnackbarNotifications } from '@ts-core/core/hooks/ui';

import { getExercisesQuery } from 'modules/exercises/graphql/queries/getExercisesQuery';

import { FillInTheBlanksExerciseInput } from '../types/form';
import { FillInTheBlanksExercise } from '../types';
import {
  createFillInTheBlanksExerciseMutation,
  CreateFillInTheBlanksExerciseMutationResult,
  CreateFillInTheBlanksExerciseMutationVariables,
} from '../graphql/mutations/createFillInTheBlanksExerciseMutation';

import {
  updateFillInTheBlanksExerciseMutation,
  UpdateFillInTheBlanksExerciseMutationResult,
  UpdateFillInTheBlanksExerciseMutationVariables,
} from '../graphql/mutations/updateFillInTheBlanksExerciseMutation';

import { transformFormInputToMutationInput } from '../utils';

interface FillInTheBlanksExerciseActionsHookInput {
  courseId: string;
  subjectId: string;
}

interface FillInTheBlanksExerciseActionsHookOutput {
  onCreateFillInTheBlanksExercise: (input: FillInTheBlanksExerciseInput, onSuccess: (exercise: FillInTheBlanksExercise) => void) => void;
  createFillInTheBlanksExerciseLoading: boolean;
  createFillInTheBlanksExerciseError?: string;

  onUpdateFillInTheBlanksExercise: (exerciseId: string, input: FillInTheBlanksExerciseInput) => void;
  updateFillInTheBlanksExerciseLoading: boolean;
  updateFillInTheBlanksExerciseError?: string;
}

export default function useFillInTheBlanksExerciseActions(input: FillInTheBlanksExerciseActionsHookInput): FillInTheBlanksExerciseActionsHookOutput {
  const { courseId, subjectId } = input;
  const { showErrorNotification, showSuccessNotification } = useSnackbarNotifications();
  const intl = useIntl();

  /**
   * Create
   */
  const [createFillInTheBlanksExercise, { loading: createFillInTheBlanksExerciseLoading, error: createFillInTheBlanksExerciseError }] = useMutation<
    CreateFillInTheBlanksExerciseMutationResult,
    CreateFillInTheBlanksExerciseMutationVariables
  >(createFillInTheBlanksExerciseMutation, {
    onError: showErrorNotification,
    onCompleted: () => showSuccessNotification(intl.formatMessage({ id: 'notification.exerciseSaved' })),
    refetchQueries: [{ query: getExercisesQuery, variables: { courseId, subjectId } }],
  });
  const onCreateFillInTheBlanksExercise = useCallback(
    (input: FillInTheBlanksExerciseInput, onSuccess: (exercise: FillInTheBlanksExercise) => void) => {
      createFillInTheBlanksExercise({
        variables: {
          courseId,
          subjectId,
          input: transformFormInputToMutationInput(input),
        },
      }).then(({ data }) => {
        if (data?.fillInTheBlanksExercise) {
          onSuccess(data.fillInTheBlanksExercise);
        }
      });
    },
    [courseId, subjectId, createFillInTheBlanksExercise]
  );

  /**
   * Update
   */
  const [updateFillInTheBlanksExercise, { loading: updateFillInTheBlanksExerciseLoading, error: updateFillInTheBlanksExerciseError }] = useMutation<
    UpdateFillInTheBlanksExerciseMutationResult,
    UpdateFillInTheBlanksExerciseMutationVariables
  >(updateFillInTheBlanksExerciseMutation, {
    onError: showErrorNotification,
    onCompleted: () => showSuccessNotification(intl.formatMessage({ id: 'notification.exerciseSaved' })),
  });
  const onUpdateFillInTheBlanksExercise = useCallback(
    (exerciseId: string, input: FillInTheBlanksExerciseInput) => {
      // const finalInput = transformFormInputToMutationInput(input);

      updateFillInTheBlanksExercise({
        variables: {
          courseId,
          subjectId,
          exerciseId,
          input: transformFormInputToMutationInput(input),
        },
      });
    },
    [updateFillInTheBlanksExercise, courseId, subjectId]
  );

  return {
    onCreateFillInTheBlanksExercise,
    createFillInTheBlanksExerciseLoading,
    createFillInTheBlanksExerciseError: getErrorMessage({ error: createFillInTheBlanksExerciseError }),

    onUpdateFillInTheBlanksExercise,
    updateFillInTheBlanksExerciseLoading,
    updateFillInTheBlanksExerciseError: getErrorMessage({ error: updateFillInTheBlanksExerciseError }),
  };
}
